'use client';
import styled from '@emotion/styled';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import imageUrlBuilder from '@sanity/image-url';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';
import { config } from '@/config/environment';

const builder = imageUrlBuilder(sanityClient);
function urlFor(source: any) {
  return builder.image(source).url();
}

const MainDiv = styled.div`
  width: auto;
  height: auto;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #231727;
  padding-bottom: 160px;
  @media (max-width: 768px) {
    padding-top: 98px;
  }
`;
const Div = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const IntroDiv = styled(Grid)`
  width: 100%;
  height: 100vh;
  max-height: 100vw;
  padding: 13.5% 12.5%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: -180px;
  @media (max-width: 768px) {
    margin-bottom: 0;
    padding: 0;
    max-height: unset;
    justify-content: flex-start;
    height: auto;
  }
`;
const TitleDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  height: auto;
  z-index: 1;
  @media (max-width: 768px) {
    padding: 0 20px;
    justify-content: flex-start;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #fdf3de;
  margin: 0;
  @media (max-width: 768px) {
    text-align: start;
    margin-bottom: 12px;
  }
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  color: #ffffff;
  text-align: start;
  width: 100%;
  @media (max-width: 768px) {
    margin-bottom: 32px;
    width: 100%;
  }
`;
const Vector = styled(Image)`
  position: absolute;
  left: 0;
  top: 128px;
  width: 38.6%;
  height: auto;
`;
Vector.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const ImageDesktop = styled(Image)`
  height: auto;
  width: 85%;
  margin-bottom: -48px;
  position: relative;
  z-index: 10;
`;
ImageDesktop.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const ImageDesktopRight = styled(Image)`
  height: auto;
  width: 85%;
  margin-left: auto;
  float: right;
  margin-bottom: -48px;
  z-index: 10;
`;
ImageDesktopRight.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const ImageMobile = styled(Image)`
  width: 90.666%;
  height: auto;
  left: 0;
  @media (max-width: 768px) {
    height: auto;
  }
`;
ImageMobile.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const ImageMobileRight = styled(ImageMobile)`
  right: 0;
  left: unset;
`;
ImageMobileRight.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const Year = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 40px;
  line-height: 112.3%;
  color: #ffffff;
  margin-bottom: 20px;
  @media (max-width: 768px) {
    text-align: start;
  }
`;
const StoryText = styled(Text)`
  font-size: 16px;
  width: 96.85%;
`;
const StoryDiv = styled(TitleDiv)`
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-right: 12.5%;
  position: relative;
  z-index: 1;
  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;
const Story2015 = styled(StoryDiv)`
  padding-top: 60px;
  @media (max-width: 768px) {
    padding-top: 40px;
  }
`;
const Story2017 = styled(StoryDiv)`
  @media (max-width: 768px) {
    padding-top: 40px;
  }
`;
const Story2019 = styled(StoryDiv)`
  padding-top: 75px;
  @media (max-width: 768px) {
    padding-top: 40px;
  }
`;
const Story2021 = styled(StoryDiv)`
  padding-top: 12.26%;
  @media (max-width: 768px) {
    padding-top: 40px;
  }
`;
const StoryDivLeft = styled(TitleDiv)`
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-left: 12.5%;
  position: relative;
  z-index: 1;
  ${StoryText} {
    width: 97.5%;
    font-size: 16px;
  }
  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;
const Story2018 = styled(StoryDivLeft)`
  padding-bottom: 30px;
  @media (max-width: 768px) {
    padding-top: 0;
  }
`;
const ImageDiv = styled(Grid)`
  height: auto;
  width: auto;
  padding: 0;
  @media (max-width: 768px) {
    order: 2;
    margin-bottom: 0;
    width: 100%;
  }
`;
const MobileVector = styled(Image)`
  left: 0;
  width: 26.66%;
  height: auto;
  margin-bottom: 32px;
`;
MobileVector.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};

const Origins = () => {
  const [originsData, setOriginsData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width:960px)');
  const archiveMode = config.platformMode === 'archive';

  useEffect(() => {
    const fetchOriginsData = async () => {
      const data = await sanityClient.fetch(
        `*[_type=="origins"][0]{ title, description, years[]{ year, story, image } }`,
      );
      setOriginsData(data);
      setLoading(false);
    };

    fetchOriginsData();
  }, []);

  if (loading || !originsData) {
    return <MainDiv>Loading...</MainDiv>;
  }

  const visibleTitle = archiveMode ? "Curiko's origins" : originsData.title;
  const visibleDescription = archiveMode
    ? 'Curiko emerged from ten years of social research, design, and development. Along the way, we tested new roles, technologies, and models of support—all aimed at addressing social and experiential isolation. Curiko brought those learnings together into a single community, reimagining social safety nets as trampolines for connection, growth, and belonging.'
    : originsData.description;

  return (
    <MainDiv>
      <IntroDiv container>
        {!isMobile && <Vector src="/about-us/origins/vector1.png" alt="" />}
        <TitleDiv item md={6} xs={12}>
          <Title>{visibleTitle}</Title>
        </TitleDiv>
        <TitleDiv item md={6} xs={12}>
          <Text>{visibleDescription}</Text>
        </TitleDiv>
      </IntroDiv>
      {isMobile && <MobileVector src={'/about-us/origins/mobile/vector.png'} alt="" />}
      {originsData.years.map((yearData: any, index: number) => (
        <Div container key={index}>
          {index % 2 === 0 ? (
            <>
              <StoryDivLeft item md={6} xs={12}>
                <Year>{yearData.year}</Year>
                <StoryText>{yearData.story}</StoryText>
              </StoryDivLeft>
              <ImageDiv item md={6} xs={12}>
                {isMobile ? (
                  <ImageMobile src={urlFor(yearData.image)} alt="" />
                ) : (
                  <ImageDesktopRight src={urlFor(yearData.image)} alt="" />
                )}
              </ImageDiv>
            </>
          ) : (
            <>
              <ImageDiv item md={6} xs={12}>
                {isMobile ? (
                  <ImageMobileRight src={urlFor(yearData.image)} alt="" />
                ) : (
                  <ImageDesktop src={urlFor(yearData.image)} alt="" />
                )}
              </ImageDiv>
              <StoryDiv item md={6} xs={12}>
                <Year>{yearData.year}</Year>
                <StoryText>{yearData.story}</StoryText>
              </StoryDiv>
            </>
          )}
        </Div>
      ))}
    </MainDiv>
  );
};

export default Origins;
